import { useEffect, useState } from 'react';

import { useAppSelector, useAppDispatch } from '@verantos-dev/app-state-base';
import {
  selectResearchAssistantMessages,
  selectResearchAssistantModel,
  setResearchAssistantIsReady,
} from '@verantos-dev/app-state-research-assistant';
import { useAuth } from '@verantos-dev/core-auth';
import { useCohortBuilderData, useSafeRouteParams } from '@verantos-dev/data-access-cohorts';

import { useResearchAssistantSocketContext } from '../../contexts/ResearchAssistantSocketContext';
import { emitSubscribeToCohort } from '../emitters/connectionEvents';

export const useResearchAssistantConnectionListeners = () => {
  const { user } = useAuth();
  const { projectId, cohortId, cohortVersionId } = useSafeRouteParams();
  const { data } = useCohortBuilderData();
  const { socket: currentSocket, cleanupSocket } = useResearchAssistantSocketContext();
  const model = useAppSelector(selectResearchAssistantModel);
  const messages = useAppSelector(selectResearchAssistantMessages);
  const dispatch = useAppDispatch();

  // if for whatever reason you get disconnected, keep state that you are reconnecting
  const [reconnecting, setReconnecting] = useState<boolean>(false);

  const isOwner =
    !!data?.data.createdBy.userId &&
    [user.auth0Id, user.userId].includes(data?.data.createdBy.userId);

  useEffect(() => {
    if (!currentSocket) return;

    const onConnect = () => {
      console.info('Connected to research assistant socket:', currentSocket.id);
      // Keep UI disabled until history/welcome is received
      dispatch(setResearchAssistantIsReady(false));
      if (projectId && cohortId && cohortVersionId && model) {
        emitSubscribeToCohort(
          currentSocket,
          projectId,
          cohortId,
          cohortVersionId,
          model,
          isOwner,
          user,
          reconnecting,
          messages || []
        );
      }
    };
    const onDisconnect = (reason: string) => {
      console.info('Disconnected from research assistant socket:', reason);
      dispatch(setResearchAssistantIsReady(false));
      setReconnecting(true);
    };

    const onSubscribe = () => {
      console.info('Subscribed to research assistant socket');
      dispatch(setResearchAssistantIsReady(true));
    };

    currentSocket.on('disconnect', onDisconnect);
    currentSocket.on('connect', onConnect);
    currentSocket.on('subscribe', onSubscribe);

    return () => {
      currentSocket.off('disconnect');
      currentSocket.off('connect');
      currentSocket.off('subscribe');
    };
  }, [
    currentSocket,
    dispatch,
    projectId,
    cohortId,
    cohortVersionId,
    model,
    user,
    messages,
    reconnecting,
  ]);

  useEffect(() => {
    return () => {
      console.info('Cleaning up research assistant socket listeners and state.');
      cleanupSocket();
    };
  }, [cleanupSocket]);
};
